class REAPopupBanner extends HTMLElement {
    static get observedAttributes() {
        return [
            'form-selector',
            'success-selector',
            'redirect-url',
            'redirect-delay-ms',
            'auto-open',
            'auto-open-delay-ms',
            'auto-open-scope',
            'campaign-utm'
        ];
    }

    connectedCallback() {
        this._content = this.querySelector('[data-ref="content"]');
        this._closeBtns = this.querySelectorAll('[data-ref="close"]');

        this._closeBtns.forEach(btn => {btn.addEventListener('click', () => this.close(), { passive: true });});
        this.addEventListener('click', (e) => {
            if (e.target === this) this.close();
        }, {passive: true});
        this._onKeydown = (e) => {
            if (e.key === 'Escape') this.close();
        };
        document.addEventListener('keydown', this._onKeydown);

        this._readAttrs();
        this.__openedOnce = false;
        this._observer = null;

        this._bindTargets();
        this._campaignSessionHandler = () => this._maybeAutoOpen();
        document.addEventListener('readers:campaign-session-change', this._campaignSessionHandler);
        this._maybeAutoOpen();
    }

    attributeChangedCallback() {
        this._disconnectObserver();
        this._readAttrs();
        this._bindTargets();
        this._maybeAutoOpen();
    }

    disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeydown);
        document.removeEventListener('readers:campaign-session-change', this._campaignSessionHandler);
        this._disconnectObserver();
    }

    open() {
        if (this.__openedOnce) return;
        this.__openedOnce = true;
        this.classList.remove('tw-hidden');
        this.removeAttribute('aria-hidden');
        queueMicrotask(() => {
            this._closeBtn && this._closeBtn.focus();
        });
    }

    close() {
        this.classList.add('tw-hidden');
        this.setAttribute('aria-hidden', 'true');
    }

    // ---------- internals ----------
    _readAttrs() {
        this._formSelector = this.getAttribute('form-selector') || '#contact_form';
        this._successSelector = this.getAttribute('success-selector') || '.newsletter-form__message--success';
        this._redirectUrl = this.getAttribute('redirect-url') || '';
        this._redirectDelayMs = parseInt(this.getAttribute('redirect-delay-ms') || '600', 10);

        this._autoOpen = (this.getAttribute('auto-open') || 'false') === 'true';
        this._autoOpenDelayMs = parseInt(this.getAttribute('auto-open-delay-ms') || '600', 10);
        this._autoOpenScope = this.getAttribute('auto-open-scope') || 'session'; // 'session' | 'always'
        this._campaignUtm = this.getAttribute('campaign-utm') || '';
    }

    _bindTargets() {
        this._form = document.querySelector(this._formSelector);
        if (!this._form) return;

        this._successEl = this._form.querySelector(this._successSelector);

        // Se già visibile al mount
        if (this._isVisible(this._successEl)) {
            this._onSuccessDetected();
            return;
        }

        if (this._successEl) {
            this._observer = new MutationObserver(() => {
                if (this._isVisible(this._successEl)) {
                    this._onSuccessDetected();
                    this._disconnectObserver();
                }
            });
            this._observer.observe(this._successEl, {attributes: true, attributeFilter: ['class', 'style']});
        }

        window.addEventListener('newsletter:success', () => this._onSuccessDetected(), {once: true});

        const formObserver = new MutationObserver(() => {
            const maybe = this._form.querySelector(this._successSelector);
            if (maybe && maybe !== this._successEl) {
                this._successEl = maybe;
                if (this._isVisible(this._successEl)) {
                    this._onSuccessDetected();
                    formObserver.disconnect();
                } else {
                    this._observer && this._observer.disconnect();
                    this._observer = new MutationObserver(() => {
                        if (this._isVisible(this._successEl)) {
                            this._onSuccessDetected();
                            this._disconnectObserver();
                            formObserver.disconnect();
                        }
                    });
                    this._observer.observe(this._successEl, {attributes: true, attributeFilter: ['class', 'style']});
                }
            }
        });
        formObserver.observe(this._form, {childList: true, subtree: true});

        // Hint URL (Shopify)
        const urlHintsSuccess = /contact_posted|form_type=customer/.test(window.location.search) && window.location.hash.includes('contact_form');
        if (urlHintsSuccess) {
            requestAnimationFrame(() => setTimeout(() => {
                if (this._isVisible(this._successEl)) this._onSuccessDetected();
            }, 0));
        }
    }

    _maybeAutoOpen() {
        if (!this._autoOpen) return;

        if (!this._canAutoOpenForCampaign()) return;

        const isInternalForm = this.contains(this._form);
        if (!isInternalForm) return;

        if (this._autoOpenScope === 'session') {
            const key = this._seenKey();
            if (this._getCookie(key) === '1') return;
            this._setCookie(key, '1', 30);
        }

        setTimeout(() => this.open(), Math.max(0, this._autoOpenDelayMs || 0));
    }

    /**
     * Decides whether the popup can auto-open for the active campaign session.
     * @returns {boolean}
     */
    _canAutoOpenForCampaign() {
        const session = this._getCampaignSession();

        if (this._campaignUtm) {
            return this._campaignMatches(session);
        }

        // Preserve the old behavior: don't auto-open the newsletter popup for UTM-targeted visitors.
        if (session.utmCampaigns.length > 0) return false;

        return true;
    }

    /**
     * Checks configured popup UTM targeting against the active session.
     * @param {{utmCampaigns: string[], hasUtmCampaign: Function}} session Campaign session.
     * @returns {boolean}
     */
    _campaignMatches(session) {
        return this._campaignUtm ? session.hasUtmCampaign(this._campaignUtm) : false;
    }

    /**
     * Reads the shared campaign session with a lightweight fallback for load-order safety.
     * @returns {{utmCampaigns: string[], hasUtmCampaign: Function}}
     */
    _getCampaignSession() {
        if (window.readersCampaignSession) {
            return window.readersCampaignSession;
        }

        const normalize = (value) => String(value || '').trim().toLowerCase();
        let utmCampaigns = [];

        try {
            utmCampaigns = JSON.parse(sessionStorage.getItem('readersCampaignUtmCampaigns') || '[]');
        } catch (_) {
            utmCampaigns = [];
        }

        const urlUtmCampaign = new URLSearchParams(window.location.search).get('utm_campaign');
        if (urlUtmCampaign) utmCampaigns.push(urlUtmCampaign);

        utmCampaigns = Array.from(new Set(utmCampaigns.map(normalize).filter(Boolean)));

        return {
            utmCampaigns,
            hasUtmCampaign: (campaign) => {
                const normalizedCampaign = normalize(campaign);
                return normalizedCampaign ? utmCampaigns.includes(normalizedCampaign) : utmCampaigns.length > 0;
            },
        };
    }

    _onSuccessDetected() {
        this.open();

        const isInternalForm = this.contains(this._form);
        if (isInternalForm && this._redirectUrl) {
            this.close();
            setTimeout(() => {
                window.location.assign(this._redirectUrl);
            }, Math.max(0, this._redirectDelayMs || 0));
        }
    }


    _disconnectObserver() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }

    _isVisible(el) {
        if (!el) return false;
        const style = getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') === 0) return false;
        return el.offsetParent !== null || style.position === 'fixed';
    }

    _seenKey() {
        const id = this.getAttribute('id') || 'rea-popup';
        return `rea_pb_seen__${id}`;
    }

    _setCookie(name, value, days) {
        try {
            const d = new Date();
            d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
            const expires = `expires=${d.toUTCString()}`;
            document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
        } catch (_) {
            // none
        }
    }

    _getCookie(name) {
        try {
            const n = encodeURIComponent(name) + '=';
            const parts = document.cookie.split(';');
            for (let c of parts) {
                while (c.charAt(0) === ' ') c = c.substring(1);
                if (c.indexOf(n) === 0) return decodeURIComponent(c.substring(n.length, c.length));
            }
        } catch (_) {
            // none
        }
        return null;
    }
}

if (!customElements.get('rea-popup-banner')) {
    customElements.define('rea-popup-banner', REAPopupBanner);
}
